import { InputV2 } from "@southfields-digital/mxmz-react-ui";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import {
	useAutocompleteState,
	useFocusManagement,
	useKeyboardNavigation,
	useSmartPositioning,
} from "../hooks";
import type { SuggestionOption } from "../types";

import { type AutocompleteContextValue, AutocompleteProvider } from "./AutocompleteContext";
import SuggestionList from "./SuggestionList";
import type { AutocompleteInputProps } from "./types";

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
	value,
	onChange,
	suggestions,
	valueExtractor = (suggestion) => suggestion.label,
	placeholder = "Type or select...",
	disabled = false,
	className = "",
	multiline = false,
	maxLength,
	variant = "text",
	onClickMedia,
	onRemoveMedia,
	minSearchLength = 0,
	maxSuggestions = 50,
	showOnFocus = false,
	filterByValue = true,
	enableSpecialCharacters = true,
	onSuggestionSelect,
	onFocus,
	onBlur,
}) => {
	// Generate unique IDs for accessibility
	const inputId = useId();
	const listboxId = useId();

	// Create ref for the editable element to enable precise focus detection
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

	// Track the last selected value to prevent immediate reopening with the same value
	const [lastSelectedValue, setLastSelectedValue] = useState<string | null>(null);

	// Enhanced autocomplete state management
	const {
		isOpen,
		setIsOpen,
		highlightedIndex,
		setHighlightedIndex,
		filteredSuggestions,
		groupedSuggestions,
		isLoading,
		error,
	} = useAutocompleteState({
		value,
		suggestions,
		minSearchLength,
		maxSuggestions,
		showOnFocus,
		filterByValue,
		inputRef,
		lastSelectedValue,
		enableSpecialCharacters,
	});

	// Focus management
	const { containerRef, handleInputFocus, handleInputBlur, cleanup } = useFocusManagement({
		onFocus,
		onBlur,
		filteredSuggestions,
		setIsOpen,
	});

	const {
		dropdownRef,
		position: dropdownPosition,
		maxHeight,
		isPositioned,
	} = useSmartPositioning({
		isOpen,
		containerRef,
	});

	// Handle input change
	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			// Clear the last selected value when user starts typing
			setLastSelectedValue(null);

			const syntheticEvent = {
				...e,
				currentTarget: e.target,
			} as React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>;

			onChange(syntheticEvent);
			setHighlightedIndex(-1);
		},
		[onChange, setHighlightedIndex, setLastSelectedValue],
	);

	// Handle suggestion selection
	const handleSuggestionSelect = useCallback(
		(suggestion: SuggestionOption) => {
			const extracted = valueExtractor(suggestion);
			// Respect the cap: native maxLength only limits typing, not selection.
			const selectedValue = maxLength && maxLength > 0 ? extracted.slice(0, maxLength) : extracted;

			// Store the selected value to prevent immediate reopening
			setLastSelectedValue(selectedValue);

			// Create a synthetic event with the selected value
			const syntheticEvent = {
				currentTarget: { value: selectedValue },
				target: { value: selectedValue },
			} as unknown as React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>;

			onChange(syntheticEvent);
			onSuggestionSelect?.(suggestion);

			setIsOpen(false);
			setHighlightedIndex(-1);
		},
		[
			onChange,
			valueExtractor,
			maxLength,
			onSuggestionSelect,
			setIsOpen,
			setHighlightedIndex,
			setLastSelectedValue,
		],
	);

	// Enhanced keyboard navigation
	const { handleKeyDown, navigateUp, navigateDown, selectHighlighted, close } =
		useKeyboardNavigation({
			isOpen,
			filteredSuggestions,
			highlightedIndex,
			setHighlightedIndex,
			setIsOpen,
			onSuggestionSelect: handleSuggestionSelect,
			multiline,
		});

	// Cleanup on unmount
	useEffect(() => cleanup(), [cleanup]);

	// Create context value
	const contextValue: AutocompleteContextValue = {
		state: {
			isOpen,
			highlightedIndex,
			filteredSuggestions,
			groupedSuggestions,
			position: dropdownPosition,
			maxHeight,
			isPositioned,
			isLoading,
			error,
		},
		actions: {
			setIsOpen,
			setHighlightedIndex,
			onSuggestionSelect: handleSuggestionSelect,
			onHighlightChange: setHighlightedIndex,
			navigateUp,
			navigateDown,
			selectHighlighted,
			close,
		},
	};

	const counter =
		maxLength && maxLength > 0 ? (
			<span
				className={`text-xs text-gray-400 select-none whitespace-nowrap${
					multiline ? " self-end mb-1 mr-3" : ""
				}`}
			>
				{value.length}/{maxLength}
			</span>
		) : undefined;

	// A literal `type` per branch is required (InputV2 is a discriminated union).
	const sharedInputProps = {
		ref: inputRef as unknown as React.Ref<HTMLInputElement>,
		id: inputId,
		value,
		onChange: handleInputChange,
		onKeyDown: handleKeyDown,
		onBlur: handleInputBlur,
		onFocus: handleInputFocus,
		placeholder,
		disabled: disabled || isLoading,
		maxLength,
		autoComplete: "off" as const,
		"aria-expanded": isOpen,
		"aria-haspopup": "listbox" as const,
		"aria-controls": isOpen ? listboxId : undefined,
		"aria-activedescendant":
			highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]
				? `suggestion-${filteredSuggestions[highlightedIndex].value}`
				: undefined,
		"aria-label": placeholder,
		"data-testid": "autocomplete-input-field",
	};

	return (
		<AutocompleteProvider value={contextValue}>
			<div
				ref={containerRef}
				className={`relative w-full bg-transparent ${className}`}
				data-testid="autocomplete-input"
			>
				{/* `selectableInput` builds its own suffix (clear + media buttons), so passing
            one — even `undefined` — would replace those buttons. */}
				{variant === "selectableInput" ? (
					<InputV2
						type="selectableInput"
						{...sharedInputProps}
						onClick={onClickMedia}
						onRemoveMedia={onRemoveMedia}
					/>
				) : multiline ? (
					<InputV2 type="textarea" inputSize="sm" {...sharedInputProps} suffix={counter} />
				) : (
					<InputV2 type="text" {...sharedInputProps} suffix={counter} />
				)}

				<div ref={dropdownRef} id={listboxId}>
					<SuggestionList />
				</div>

				{isLoading && (
					<div className="absolute right-2 top-1/2 transform -translate-y-1/2">
						<div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-transparent rounded-full" />
					</div>
				)}
			</div>
		</AutocompleteProvider>
	);
};

export default AutocompleteInput;
